import { ColDef, GetContextMenuItemsParams, GridOptions } from '@ag-grid-community/core';
import { commonGridOptions } from '../blotter.service.utils';
import { EBlotterTab } from '../../../enums';
import { getKondorBlotterColumnDefs } from '.';
import { GRID_CONTEXT_MENU_SERVICE, IGridContextMenuService } from '../../grid-context-menu';
import { IKondorBlotterService } from './kondor-blotter.interface';
import { IKondorColumn, IKondorRow } from '../../../interfaces';
import { inject, injectable } from 'inversify';
import {
    ISSEForKondorBlotterService,
    SSE_FOR_KONDOR_BLOTTER_SERVICE,
} from '../../sse-for-kondor-blotter';
import { map, takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject, Subject } from 'rxjs';

@injectable()
export class KondorBlotterService implements IKondorBlotterService {

    private dataFeedObs$: ReplaySubject<IKondorRow>;
    private columnsFeedObs$: ReplaySubject<Array<ColDef>>;
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    private columnDefs: Array<ColDef>;

    type = 'kondor' as EBlotterTab;
    isRowActionsEnabled: boolean;

    @inject(SSE_FOR_KONDOR_BLOTTER_SERVICE) private readonly sseSrv: ISSEForKondorBlotterService;
    @inject(GRID_CONTEXT_MENU_SERVICE) gridContextMenuSrv: IGridContextMenuService;

    async init(): Promise<void> {
        this.dataFeedObs$ = new ReplaySubject<IKondorRow>();
        this.columnsFeedObs$ = new ReplaySubject<Array<ColDef>>();

        this.sseSrv.addEventListener('SCHEMA_DEFINITION', (event: MessageEvent) => {
            const columnNames = event.data?.blotterColumnInfoList
                ?.map((column: IKondorColumn) => column.columnName);
            this.columnDefs = getKondorBlotterColumnDefs(columnNames);
            this.columnsFeedObs$.next(this.columnDefs);
        });

        this.sseSrv.addEventListener('BLOTTER_ROW', (event: MessageEvent) => {
            this.dataFeedObs$.next(event.data);
        });

        this.sseSrv.clearObservable()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.dataFeedObs$.complete();
                this.dataFeedObs$ = new ReplaySubject<IKondorRow>();
            });

        return this.sseSrv.init();
    }

    stop(): void {
        this.destroy$.next(true);
        this.sseSrv.stop();
    }

    gridOptions(): GridOptions {
        return {
            ...commonGridOptions,
            suppressFieldDotNotation: true,
            rowSelection: 'single',
            getContextMenuItems: (params: GetContextMenuItemsParams) => {
                return this.gridContextMenuSrv.getMenuItems({
                    gridType: this.type,
                    defaultItems: params.defaultItems,
                    dataItem: params.node?.data,
                });
            },
            columnDefs: this.columnDefs,
        };
    }

    ordersObservable(): Observable<IKondorRow> {
        return this.dataFeedObs$
            .pipe(
                map(row => ({ ...row, data: { ...row.data, id: row.dealStamp } })),
            );
    }

    columnDefinitions(): Observable<Array<ColDef>> {
        return this.columnsFeedObs$;
    }

    clearObservable(): Observable<void> {
        return this.sseSrv.clearObservable();
    }
}
